<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-75">
    <!-- Overlay -->
    <div class="absolute inset-0" @click="cancelModal"></div>

    <!-- Modal Container -->
    <div class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 rounded-xl shadow-2xl text-white overflow-hidden flex flex-col">
      <!-- Header -->
      <div class="flex justify-between items-center p-6 border-b border-gray-700">
        <h2 id="modal-title" class="text-xl font-semibold text-indigo-300">สร้างแผนที่สถานที่</h2>
        <button @click="cancelModal" class="text-gray-400 hover:text-white focus:outline-none" aria-label="ปิด">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Tab Navigation -->
      <div class="flex border-b border-gray-700 bg-gray-800">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          class="flex-1 px-4 py-3 text-sm font-medium text-white transition duration-200"
          :class="{
            'bg-indigo-600 border-b-2 border-indigo-500': currentTab === tab.id,
            'bg-gray-600 hover:bg-gray-500': currentTab !== tab.id
          }"
        >
          {{ tab.name }}
        </button>
      </div>

      <!-- Tab Content -->
      <div class="h-96 overflow-y-auto p-6 scroll-smooth scrollbar-thin scrollbar-thumb-indigo-500 scrollbar-track-gray-700">
        <form @submit.prevent="handleAddMap">
          <!-- Basic Info Tab -->
          <div v-if="currentTab === 'basic'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">ข้อมูลพื้นฐาน</h3>
            <div class="grid grid-cols-1 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ประเภทแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model="type"
                  type="text"
                  readonly
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 text-white cursor-not-allowed"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model="name"
                  type="text"
                  placeholder="เช่น ป่ามืด"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลิงก์รูปภาพ <span class="text-red-500">*</span></label>
                <input
                  v-model="imageUrl"
                  type="url"
                  placeholder="https://example.com/map.jpg"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <div v-if="imageUrl" class="mt-2">
                  <img :src="imageUrl" alt="Map Preview" class="w-24 h-24 object-cover rounded-lg" @error="imageUrl = ''" />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เลเวลที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="requiredLevel"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนห้องที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="roomRequired"
                  type="number"
                  min="1"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">พลังงานที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="requiredEnergy"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ลดความสุข <span class="text-red-500">*</span></label>
                <input
                  v-model.number="reduceHappy"
                  type="number"
                  min="0"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อเสียงที่ต้องการ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="requiredRenown"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนที่สามารถหนีโดยใช้เหรียญได้ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="escapeAttempts"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเหรียญที่ใช้สำหรับหนี <span class="text-red-500">*</span></label>
                <input
                  v-model.number="escapeCoins"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <h4 class="text-md font-medium text-gray-300 mt-4">ค่าเข้า</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เหรียญ <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredCoins"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">ทอง <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredGold"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">เพชร <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredDiamonds"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1">แคช <span class="text-red-500">*</span></label>
                  <input
                    v-model.number="requiredCash"
                    type="number"
                    min="0"
                    placeholder="0"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                    required
                    aria-required="true"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">แผนที่ที่ต้องมีก่อนหน้า</label>
                <input
                  v-model="requiredMap"
                  type="text"
                  placeholder="เช่น บ้านร้าง (เว้นว่างได้)"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ตึกที่อนุญาตให้สำรวจ <span class="text-gray-500 text-xs font-normal">(ไม่เลือก = ทุกตึก)</span></label>
                <div class="flex items-center gap-4 mt-2">
                  <label class="inline-flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :value="1"
                      v-model="allowedBuildings"
                      class="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span class="text-sm text-gray-300">ตึก 1</span>
                  </label>
                  <label class="inline-flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      :value="2"
                      v-model="allowedBuildings"
                      class="form-checkbox h-5 w-5 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                    />
                    <span class="text-sm text-gray-300">ตึก 2</span>
                  </label>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">สถานะแผนที่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isActive ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleActive"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isActive ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะแผนที่"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isActive ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอพ่อค้าเร่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isMerchantEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleMerchantEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isMerchantEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะพ่อค้าเร่"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isMerchantEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอชาวบ้าน <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isVillagerEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleVillagerEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isVillagerEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะชาวบ้าน"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isVillagerEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">เปิดใช้งานการเจอสิ่งมีชีวิตกลายพันธุ์ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isMutantEnabled ? 'ใช้งาน' : 'ไม่ใช้งาน' }}</span>
                  <button
                    type="button"
                    @click="toggleMutantEnabled"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isMutantEnabled ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะสิ่งมีชีวิตกลายพันธุ์"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isMutantEnabled ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">อนุญาตให้ยึดครองแผนที่ <span class="text-red-500">*</span></label>
                <div class="flex items-center space-x-4">
                  <span class="text-sm text-gray-300">{{ isOccupationAllowed ? 'อนุญาต' : 'ไม่อนุญาต' }}</span>
                  <button
                    type="button"
                    @click="toggleOccupationAllowed"
                    class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    :class="isOccupationAllowed ? 'bg-indigo-600' : 'bg-gray-600'"
                    aria-label="สลับสถานะการยึดครอง"
                  >
                    <span
                      class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200"
                      :class="isOccupationAllowed ? 'translate-x-6' : 'translate-x-1'"
                    />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">UID ผู้ยึดครองแผนที่ปัจจุบัน</label>
                <input
                  v-model="occupierUid"
                  type="text"
                  placeholder="UID ของผู้เล่น (เว้นว่างหากไม่มี)"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">คะแนนยึดครองแผนที่ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="occupationScore"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">ค่าใช้จ่ายในการท้าประลอง (เพชร) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="challengeCostDiamonds"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">รางวัลรายวันสำหรับผู้ยึดครอง (เพชร) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="dailyDiamondReward"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสเจอผู้ยึดครอง (%) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="encounterChance"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="1"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
            </div>
          </div>

          <!-- Zombies Tab -->
          <div v-if="currentTab === 'zombies'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">ซอมบี้ในแผนที่</h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้ขั้นต่ำ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="minZombies"
                  type="number"
                  min="1"
                  placeholder="5"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้สูงสุด <span class="text-red-500">*</span></label>
                <input
                  v-model.number="maxZombies"
                  type="number"
                  :min="minZombies"
                  placeholder="10"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <p v-if="maxZombies < minZombies" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
              </div>
            </div>
            <div class="space-y-3">
              <div
                v-for="(zombie, index) in zombies"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="zombie.name"
                    type="text"
                    placeholder="ชื่อซอมบี้ (เช่น Zombie Basic)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="zombie.chance"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="%"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeZombie(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบซอมบี้"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addZombie"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มซอมบี้
                </button>
                <button
                  @click="normalizeChances"
                  type="button"
                  class="px-4 py-2 bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-white transition duration-200"
                >
                  คำนวณโอกาส
                </button>
              </div>
              <div v-if="totalChance > 0" class="text-sm text-gray-400 mt-2">
                <p>โอกาสการเจอซอมบี้ (รวม: {{ totalChance.toFixed(2) }}%):</p>
                <p v-if="remainingChance > 0" class="text-yellow-500">เหลือ: {{ remainingChance.toFixed(2) }}%</p>
                <p v-else-if="remainingChance < 0" class="text-red-500">เกิน: {{ Math.abs(remainingChance).toFixed(2) }}%</p>
                <ul class="list-disc list-inside">
                  <li v-for="(zombie, index) in zombies" :key="index">
                    {{ zombie.name || 'ยังไม่ระบุ' }}: {{ zombie.chance || 0 }}%
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Merchants Tab -->
          <div v-if="currentTab === 'merchants'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">พ่อค้าเร่ในแผนที่</h3>
            <div v-if="!isMerchantEnabled" class="text-red-500 mb-4">พ่อค้าเร่ถูกปิดใช้งาน</div>
            <div v-else class="space-y-3">
              <div
                v-for="(merchant, index) in merchants"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="merchant.name"
                    type="text"
                    placeholder="ชื่อพ่อค้าเร่ (เช่น Merchant Basic)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="merchant.chance"
                    type="number"
                    min="1"
                    max="1000"
                    placeholder="1-1000 (1000 = 10%)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeMerchant(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบพ่อค้าเร่"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addMerchant"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มพ่อค้าเร่
                </button>
              </div>
            </div>
          </div>

          <!-- Mutants Tab -->
          <div v-if="currentTab === 'mutants'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">สิ่งมีชีวิตกลายพันธุ์ในแผนที่</h3>
            <div v-if="!isMutantEnabled" class="text-red-500 mb-4">สิ่งมีชีวิตกลายพันธุ์ถูกปิดใช้งาน</div>
            <div v-else class="space-y-3">
              <div
                v-for="(mutant, index) in mutants"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="mutant.name"
                    type="text"
                    placeholder="ชื่อสิ่งมีชีวิตกลายพันธุ์ (เช่น Mutant Basic)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="mutant.chance"
                    type="number"
                    min="1"
                    max="1000"
                    placeholder="1-1000 (1000 = 10%)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeMutant(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบสิ่งมีชีวิตกลายพันธุ์"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addMutant"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มสิ่งมีชีวิตกลายพันธุ์
                </button>
              </div>
            </div>
          </div>

          <!-- Villagers Tab -->
          <div v-if="currentTab === 'villagers'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">ชาวบ้านในแผนที่</h3>
            <div v-if="!isVillagerEnabled" class="text-red-500 mb-4">ชาวบ้านถูกปิดใช้งาน</div>
            <div v-else class="space-y-3">
              <div
                v-for="(villager, index) in villagers"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="villager.name"
                    type="text"
                    placeholder="ชื่อชาวบ้าน (เช่น Villager Basic)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="villager.chance"
                    type="number"
                    min="1"
                    max="10000"
                    placeholder="1-10000"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    v-if="index > 0"
                    @click="removeVillager(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบชาวบ้าน"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addVillager"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มชาวบ้าน
                </button>
              </div>
            </div>
          </div>

          <!-- Rewards Tab -->
          <div v-if="currentTab === 'rewards'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">รางวัลเมื่อสำเร็จ</h3>
            <h4 class="text-md font-medium text-gray-300 mt-4">รางวัลเพชร</h4>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเพชรขั้นต่ำ <span class="text-red-500">*</span></label>
                <input
                  v-model.number="diamondDropMin"
                  type="number"
                  min="0"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนเพชรสูงสุด <span class="text-red-500">*</span></label>
                <input
                  v-model.number="diamondDropMax"
                  type="number"
                  :min="diamondDropMin"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
                <p v-if="diamondDropMax < diamondDropMin" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสได้รับเพชร (%) <span class="text-red-500">*</span></label>
                <input
                  v-model.number="diamondDropChance"
                  type="number"
                  min="0"
                  max="100"
                  placeholder="0"
                  class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  required
                  aria-required="true"
                />
              </div>
            </div>
            <div class="space-y-3">
              <div
                v-for="(reward, index) in rewards"
                :key="index"
                class="grid grid-cols-12 items-center gap-3 animate-fade-in"
              >
                <div class="col-span-6 sm:col-span-7">
                  <input
                    v-model="reward.itemName"
                    type="text"
                    placeholder="ชื่ออุปกรณ์ (เช่น กระสุน)"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-4 sm:col-span-3">
                  <input
                    v-model.number="reward.dropChance"
                    type="number"
                    min="0"
                    max="10000"
                    placeholder="จาก 10,000"
                    class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                  />
                </div>
                <div class="col-span-2">
                  <button
                    @click="removeReward(index)"
                    type="button"
                    class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                    aria-label="ลบรางวัล"
                  >
                    ลบ
                  </button>
                </div>
              </div>
              <button
                @click="addReward"
                type="button"
                class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
              >
                + เพิ่มรางวัล
              </button>
            </div>
          </div>

          <!-- Situations Tab -->
          <div v-if="currentTab === 'situations'" class="space-y-4">
            <h3 class="text-lg font-medium text-gray-300 sticky top-0 bg-gray-800 z-10 py-2">สถานการณ์ในแผนที่</h3>
            <div class="space-y-3">
              <div
                v-for="(situation, situationIndex) in situations"
                :key="situationIndex"
                class="p-4 bg-gray-900 bg-opacity-50 rounded-lg transition-all duration-200 animate-fade-in"
              >
                <div class="space-y-4">
                  <div class="grid grid-cols-1 gap-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1">ชื่อสถานการณ์ <span class="text-red-500">*</span></label>
                      <input
                        v-model="situation.name"
                        type="text"
                        placeholder="เช่น ฝูงซอมบี้บุก"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้ขั้นต่ำ <span class="text-red-500">*</span></label>
                        <input
                          v-model.number="situation.minZombies"
                          type="number"
                          min="1"
                          placeholder="5"
                          class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                          required
                          aria-required="true"
                        />
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-300 mb-1">จำนวนซอมบี้สูงสุด <span class="text-red-500">*</span></label>
                        <input
                          v-model.number="situation.maxZombies"
                          type="number"
                          :min="situation.minZombies"
                          placeholder="10"
                          class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                          required
                          aria-required="true"
                        />
                        <p v-if="situation.maxZombies < situation.minZombies" class="text-red-500 text-sm mt-1">ต้องมากกว่าหรือเท่ากับขั้นต่ำ</p>
                      </div>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-300 mb-1">โอกาสเกิด (%) <span class="text-red-500">*</span></label>
                      <input
                        v-model.number="situation.chance"
                        type="number"
                        min="0"
                        max="100"
                        placeholder="10"
                        class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>
                  <div class="mt-4">
                    <h4 class="text-md font-medium text-gray-300 mb-2">รางวัลของสถานการณ์</h4>
                    <div class="space-y-3">
                      <div
                        v-for="(reward, rewardIndex) in situation.rewards"
                        :key="rewardIndex"
                        class="p-3 bg-gray-800 bg-opacity-50 rounded-lg"
                      >
                        <div class="grid grid-cols-12 items-center gap-3">
                          <div class="col-span-6 sm:col-span-7">
                            <input
                              v-model="reward.itemName"
                              type="text"
                              placeholder="ชื่อรางวัล (เช่น กระสุน)"
                              class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                            />
                          </div>
                          <div class="col-span-4 sm:col-span-3">
                            <input
                              v-model.number="reward.dropChance"
                              type="number"
                              min="0"
                              max="10000"
                              placeholder="จาก 10,000"
                              class="w-full px-4 py-2 bg-gray-700 rounded-lg border border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none text-white"
                            />
                          </div>
                          <div class="col-span-2">
                            <button
                              @click="removeSituationReward(situationIndex, rewardIndex)"
                              type="button"
                              class="w-full px-2 py-1.5 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                              aria-label="ลบรางวัล"
                            >
                              ลบ
                            </button>
                          </div>
                        </div>
                      </div>
                      <button
                        @click="addSituationReward(situationIndex)"
                        type="button"
                        class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                      >
                        + เพิ่มรางวัล
                      </button>
                    </div>
                  </div>
                  <div class="flex justify-end mt-3">
                    <button
                      v-if="situationIndex > 0"
                      @click="removeSituation(situationIndex)"
                      type="button"
                      class="px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 text-white transition duration-200"
                      aria-label="ลบสถานการณ์"
                    >
                      ลบสถานการณ์
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex gap-3 mt-3">
                <button
                  @click="addSituation"
                  type="button"
                  class="px-4 py-2 bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-white transition duration-200"
                >
                  + เพิ่มสถานการณ์
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      <!-- Fixed Action Buttons -->
      <div class="flex justify-end gap-3 p-6 border-t border-gray-700 bg-gray-800">
        <button
          @click="cancelModal"
          type="button"
          class="px-4 py-2 bg-gray-600 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 text-white transition duration-200"
        >
          ยกเลิก
        </button>
        <button
          type="submit"
          @click="handleAddMap"
          class="px-4 py-2 bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-white transition duration-200"
          :disabled="totalChance > 100 || maxZombies < minZombies || diamondDropMax < diamondDropMin || hasInvalidSituations"
        >
          สร้างแผนที่
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { projectFirestore } from '@/firebase/config';
import { ref, computed } from 'vue';

export default {
  setup(props, context) {
    const cancelModal = () => {
      context.emit('cancelModal');
    };

    const type = ref('สถานที่');
    const name = ref('');
    const imageUrl = ref('');
    const requiredLevel = ref(1);
    const roomRequired = ref(1);
    const requiredEnergy = ref(0);
    const reduceHappy = ref(1);
    const requiredCoins = ref(0);
    const requiredGold = ref(0);
    const requiredDiamonds = ref(0);
    const requiredCash = ref(0);
    const requiredMap = ref('');
    const allowedBuildings = ref([]);
    const requiredRenown = ref(0);
    const escapeAttempts = ref(0);
    const escapeCoins = ref(0);
    const isActive = ref(true);
    const isMerchantEnabled = ref(true);
    const isVillagerEnabled = ref(true);
    const isMutantEnabled = ref(true);
    const isOccupationAllowed = ref(false);
    const occupierUid = ref('');
    const occupationScore = ref(0);
    const challengeCostDiamonds = ref(0);
    const dailyDiamondReward = ref(0);
    const encounterChance = ref(1);
    const minZombies = ref(5);
    const maxZombies = ref(10);
    const zombies = ref([{ name: '', chance: 0 }]);
    const merchants = ref([{ name: '', chance: 0 }]);
    const mutants = ref([{ name: '', chance: 0 }]);
    const villagers = ref([{ name: '', chance: 0 }]);
    const rewards = ref([{ itemName: '', dropChance: 0 }]);
    const situations = ref([{ name: '', minZombies: 1, maxZombies: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] }]);
    const diamondDropMin = ref(0);
    const diamondDropMax = ref(0);
    const diamondDropChance = ref(0);

    // Tab Management
    const tabs = [
      { id: 'basic', name: 'ข้อมูลพื้นฐาน' },
      { id: 'zombies', name: 'ซอมบี้ในแผนที่' },
      { id: 'merchants', name: 'พ่อค้าเร่' },
      { id: 'mutants', name: 'สิ่งมีชีวิตกลายพันธุ์' },
      { id: 'villagers', name: 'ชาวบ้าน' },
      { id: 'rewards', name: 'รางวัล' },
      { id: 'situations', name: 'สถานการณ์' }
    ];
    const currentTab = ref('basic');

    const switchTab = (tabId) => {
      currentTab.value = tabId;
      const scrollableDiv = document.querySelector('.overflow-y-auto');
      if (scrollableDiv) {
        scrollableDiv.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const totalChance = computed(() => {
      return zombies.value.reduce((sum, zombie) => sum + (zombie.chance || 0), 0);
    });

    const remainingChance = computed(() => 100 - totalChance.value);

    const hasInvalidSituations = computed(() => {
      return situations.value.some(s => s.maxZombies < s.minZombies);
    });

    const normalizeChances = () => {
      const currentTotal = totalChance.value;

      if (currentTotal === 0) {
        const baseChance = Math.floor(100 / zombies.value.length);
        let remaining = 100 - baseChance * zombies.value.length;

        zombies.value.forEach((zombie, index) => {
          zombie.chance = baseChance + (index < remaining ? 1 : 0);
        });
      } else {
        const scaleFactor = 100 / currentTotal;
        const scaledChances = zombies.value.map(zombie => (zombie.chance || 0) * scaleFactor);
        const flooredChances = scaledChances.map(chance => Math.floor(chance));
        const totalFloored = flooredChances.reduce((sum, chance) => sum + chance, 0);
        const remaining = 100 - totalFloored;

        const fractionalParts = scaledChances.map((chance, index) => ({
          index,
          fractional: chance - flooredChances[index],
        })).sort((a, b) => b.fractional - a.fractional);

        for (let i = 0; i < remaining; i++) {
          flooredChances[fractionalParts[i].index]++;
        }

        zombies.value.forEach((zombie, index) => {
          zombie.chance = flooredChances[index];
        });
      }
    };

    const addZombie = () => {
      zombies.value.push({ name: '', chance: 0 });
    };

    const removeZombie = (index) => {
      if (zombies.value.length > 1) {
        zombies.value.splice(index, 1);
      }
    };

    const addMerchant = () => {
      merchants.value.push({ name: '', chance: 0 });
    };

    const removeMerchant = (index) => {
      if (merchants.value.length > 1) {
        merchants.value.splice(index, 1);
      }
    };

    const addMutant = () => {
      mutants.value.push({ name: '', chance: 0 });
    };

    const removeMutant = (index) => {
      if (mutants.value.length > 1) {
        mutants.value.splice(index, 1);
      }
    };

    const addVillager = () => {
      villagers.value.push({ name: '', chance: 0 });
    };

    const removeVillager = (index) => {
      if (villagers.value.length > 1) {
        villagers.value.splice(index, 1);
      }
    };

    const addReward = () => {
      rewards.value.push({ itemName: '', dropChance: 0 });
    };

    const removeReward = (index) => {
      if (rewards.value.length > 1) {
        rewards.value.splice(index, 1);
      }
    };

    const addSituation = () => {
      situations.value.push({ name: '', minZombies: 1, maxZombies: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] });
    };

    const removeSituation = (index) => {
      if (situations.value.length > 1) {
        situations.value.splice(index, 1);
      }
    };

    const addSituationReward = (situationIndex) => {
      situations.value[situationIndex].rewards.push({ itemName: '', dropChance: 0 });
    };

    const removeSituationReward = (situationIndex, rewardIndex) => {
      if (situations.value[situationIndex].rewards.length > 1) {
        situations.value[situationIndex].rewards.splice(rewardIndex, 1);
      }
    };

    const toggleActive = () => {
      isActive.value = !isActive.value;
    };

    const toggleMerchantEnabled = () => {
      isMerchantEnabled.value = !isMerchantEnabled.value;
    };

    const toggleVillagerEnabled = () => {
      isVillagerEnabled.value = !isVillagerEnabled.value;
    };

    const toggleMutantEnabled = () => {
      isMutantEnabled.value = !isMutantEnabled.value;
    };

    const toggleOccupationAllowed = () => {
      isOccupationAllowed.value = !isOccupationAllowed.value;
    };

    const handleAddMap = async () => {
      if (totalChance.value > 100) {
        alert('โอกาสการเจอซอมบี้รวมกันต้องไม่เกิน 100%');
        return;
      }

      if (maxZombies.value < minZombies.value) {
        alert('จำนวนซอมบี้สูงสุดต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }

      if (diamondDropMax.value < diamondDropMin.value) {
        alert('จำนวนเพชรสูงสุดต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }

      if (hasInvalidSituations.value) {
        alert('จำนวนซอมบี้สูงสุดในสถานการณ์ต้องมากกว่าหรือเท่ากับจำนวนขั้นต่ำ');
        return;
      }

      try {
        const filteredRewards = rewards.value.filter(r => r.itemName && r.dropChance > 0);
        const filteredZombies = zombies.value.filter(z => z.name && z.chance > 0);
        const filteredMerchants = isMerchantEnabled.value ? merchants.value.filter(m => m.name && m.chance > 0) : [];
        const filteredMutants = isMutantEnabled.value ? mutants.value.filter(m => m.name && m.chance > 0) : [];
        const filteredVillagers = isVillagerEnabled.value ? villagers.value.filter(v => v.name && v.chance > 0) : [];
        const filteredSituations = situations.value
          .filter(s => s.name && s.chance > 0)
          .map(s => ({
            name: s.name,
            minZombies: s.minZombies,
            maxZombies: s.maxZombies,
            chance: s.chance,
            rewards: s.rewards.filter(r => r.itemName && r.dropChance > 0),
          }));
        const mapData = {
          type: type.value,
          name: name.value,
          imageUrl: imageUrl.value,
          requiredLevel: requiredLevel.value,
          roomRequired: roomRequired.value,
          requiredEnergy: requiredEnergy.value,
          reduceHappy: reduceHappy.value,
          requiredCoins: requiredCoins.value,
          requiredGold: requiredGold.value,
          requiredDiamonds: requiredDiamonds.value,
          requiredCash: requiredCash.value,
          requiredRenown: requiredRenown.value,
          requiredMap: requiredMap.value || null,
          allowedBuildings: allowedBuildings.value.length > 0 ? allowedBuildings.value.map(Number) : [],
          escapeAttempts: escapeAttempts.value,
          escapeCoins: escapeCoins.value,
          isActive: isActive.value,
          isMerchantEnabled: isMerchantEnabled.value,
          isMutantEnabled: isMutantEnabled.value,
          isVillagerEnabled: isVillagerEnabled.value,
          minZombies: minZombies.value,
          maxZombies: maxZombies.value,
          zombies: filteredZombies.map(z => ({ name: z.name, chance: z.chance || 0 })),
          zombieNames: filteredZombies.map(z => z.name),
          merchants: filteredMerchants.map(m => ({ name: m.name, chance: m.chance || 0 })),
          merchantNames: filteredMerchants.map(m => m.name),
          mutants: filteredMutants.map(m => ({ name: m.name, chance: m.chance || 0 })),
          mutantNames: filteredMutants.map(m => m.name),
          villagers: filteredVillagers.map(v => ({ name: v.name, chance: v.chance || 0 })),
          villagerNames: filteredVillagers.map(v => v.name),
          rewards: filteredRewards,
          rewardItemNames: filteredRewards.map(r => r.itemName),
          situations: filteredSituations,
          situationNames: filteredSituations.map(s => s.name),
          diamondDropMin: diamondDropMin.value,
          diamondDropMax: diamondDropMax.value,
          diamondDropChance: diamondDropChance.value,
          isOccupationAllowed: isOccupationAllowed.value,
          occupierUid: occupierUid.value || null,
          occupationScore: occupationScore.value,
          challengeCostDiamonds: challengeCostDiamonds.value,
          dailyDiamondReward: dailyDiamondReward.value,
          encounterChance: encounterChance.value,
          createdAt: new Date(),
        };

        await projectFirestore.collection('maps').doc(name.value).set(mapData);
        context.emit('mapAdded');

        // Reset form
        type.value = 'สถานที่';
        name.value = '';
        imageUrl.value = '';
        requiredLevel.value = 1;
        roomRequired.value = 1;
        requiredEnergy.value = 0;
        reduceHappy.value = 1;
        requiredCoins.value = 0;
        requiredGold.value = 0;
        requiredDiamonds.value = 0;
        requiredCash.value = 0;
        requiredRenown.value = 0;
        requiredMap.value = '';
        allowedBuildings.value = [];
        escapeAttempts.value = 0;
        escapeCoins.value = 0;
        isActive.value = true;
        isMerchantEnabled.value = true;
        isMutantEnabled.value = true;
        isVillagerEnabled.value = true;
        isOccupationAllowed.value = false;
        occupierUid.value = '';
        occupationScore.value = 0;
        challengeCostDiamonds.value = 0;
        dailyDiamondReward.value = 0;
        encounterChance.value = 1;
        minZombies.value = 5;
        maxZombies.value = 10;
        zombies.value = [{ name: '', chance: 0 }];
        merchants.value = [{ name: '', chance: 0 }];
        mutants.value = [{ name: '', chance: 0 }];
        villagers.value = [{ name: '', chance: 0 }];
        rewards.value = [{ itemName: '', dropChance: 0 }];
        situations.value = [{ name: '', minZombies: 1, maxZombies: 1, chance: 0, rewards: [{ itemName: '', dropChance: 0 }] }];
        diamondDropMin.value = 0;
        diamondDropMax.value = 0;
        diamondDropChance.value = 0;
      } catch (error) {
        console.error('Error adding map:', error);
      }
    };

    return {
      cancelModal,
      type,
      name,
      imageUrl,
      requiredLevel,
      roomRequired,
      requiredEnergy,
      reduceHappy,
      requiredCoins,
      requiredGold,
      requiredDiamonds,
      requiredCash,
      requiredMap,
      allowedBuildings,
      requiredRenown,
      escapeAttempts,
      escapeCoins,
      isActive,
      isMerchantEnabled,
      isVillagerEnabled,
      isMutantEnabled,
      isOccupationAllowed,
      occupierUid,
      occupationScore,
      challengeCostDiamonds,
      dailyDiamondReward,
      encounterChance,
      minZombies,
      maxZombies,
      zombies,
      merchants,
      mutants,
      villagers,
      rewards,
      situations,
      diamondDropMin,
      diamondDropMax,
      diamondDropChance,
      totalChance,
      remainingChance,
      hasInvalidSituations,
      normalizeChances,
      addZombie,
      removeZombie,
      addMerchant,
      removeMerchant,
      addMutant,
      removeMutant,
      addVillager,
      removeVillager,
      addReward,
      removeReward,
      addSituation,
      removeSituation,
      addSituationReward,
      removeSituationReward,
      toggleActive,
      toggleMerchantEnabled,
      toggleVillagerEnabled,
      toggleMutantEnabled,
      toggleOccupationAllowed,
      handleAddMap,
      tabs,
      currentTab,
      switchTab
    };
  },
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.scroll-smooth {
  scroll-behavior: smooth;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #6366f1 #4b5563;
}

.scrollbar-thin::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #4b5563;
  border-radius: 4px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 4px;
  border: 2px solid #4b5563;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #818cf8;
}
</style>